import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, FilePdf, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BudgetVsActual = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/budget-vs-actual`, { withCredentials: true });
      setData(res.data);
    } catch { toast.error('Failed to load budget data'); }
    finally { setLoading(false); }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/projects/${projectId}/budget-vs-actual/export/${format}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BudgetVsActual.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-600">Loading...</div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-zinc-500">No budget data available</div>;

  const b = data.budgeted;
  const a = data.actual;
  const v = data.variance;

  const matPercent = b.material > 0 ? Math.min(100, (a.material / b.material) * 100) : 0;
  const isOverBudget = v.material < 0;

  return (
    <div>
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <Button onClick={() => navigate(`/projects/${projectId}?tab=control`)} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
                <ArrowLeft size={18} className="mr-1.5" /> Back to Control Tower
              </Button>
              <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Budget vs Actual</h1>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleExport('pdf')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100" data-testid="export-bva-pdf-button">
                <FilePdf size={20} className="mr-2" /> PDF
              </Button>
              <Button onClick={() => handleExport('excel')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100" data-testid="export-bva-excel-button">
                <FileXls size={20} className="mr-2" /> Excel
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ModuleGuide text="Compare budgeted costs from the BOQ against actual spending. Monitor material variance to prevent overruns." />

        {/* Top-Level Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <SummaryCard label="Budgeted Total" value={b.total} color="blue" testId="budgeted-total" />
          <SummaryCard label="Budgeted Material" value={b.material} color="zinc" testId="budgeted-material" />
          <SummaryCard label="Actual Material Spent" value={a.material} color={isOverBudget ? 'red' : 'green'} testId="actual-material" />
          <SummaryCard label="Variation Orders" value={data.variation_orders_total} color="amber" testId="vo-total" />
        </div>

        {/* Material Variance Bar */}
        <div className="bg-white border border-zinc-200 p-5 mb-8" data-testid="material-variance-section">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-3">Material Spend Progress</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="h-4 bg-zinc-100 rounded-sm overflow-hidden">
                <div className={`h-full transition-all duration-500 ${isOverBudget ? 'bg-red-500' : matPercent > 80 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${matPercent}%` }} />
              </div>
              <div className="flex justify-between mt-1 text-xs text-zinc-500">
                <span>₹0</span>
                <span>Budget: ₹{b.material.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="text-right min-w-[120px]">
              <p className={`text-lg font-bold ${isOverBudget ? 'text-red-600' : 'text-green-600'}`} data-testid="variance-amount">
                {isOverBudget ? '−' : '+'}₹{Math.abs(v.material).toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-zinc-500">{v.material_percentage}% variance</p>
            </div>
          </div>
        </div>

        {/* Budget Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <BreakdownCard label="Material Budget" budgeted={b.material} actual={a.material} testId="bd-material" />
          <BreakdownCard label="Labour Budget" budgeted={b.labour} actual={a.manpower} testId="bd-labour" />
          <BreakdownCard label="Margin Budget" budgeted={b.margin} actual={null} testId="bd-margin" />
        </div>

        {/* Trade-wise Breakdown */}
        {data.trade_breakdown && data.trade_breakdown.length > 0 && (
          <div className="bg-white border border-zinc-200 p-5" data-testid="trade-breakdown">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">Trade-wise Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    {['Trade', 'Budgeted Material', 'Budgeted Labour', 'Budgeted Margin', 'Actual Material', 'Variance'].map((h) => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.trade_breakdown.map((t, i) => {
                    const tvar = t.budgeted_material - t.actual_material;
                    return (
                      <tr key={i} className="border-b border-zinc-100" data-testid={`trade-row-${i}`}>
                        <td className="px-4 py-3 font-medium">{t.trade}</td>
                        <td className="px-4 py-3">₹{t.budgeted_material.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">₹{t.budgeted_labour.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">₹{t.budgeted_margin.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">₹{t.actual_material.toLocaleString('en-IN')}</td>
                        <td className={`px-4 py-3 font-medium ${tvar < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {tvar < 0 ? '−' : '+'}₹{Math.abs(tvar).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const SummaryCard = ({ label, value, color, testId }) => {
  const colorMap = {
    blue: 'text-blue-700', green: 'text-green-700', red: 'text-red-600',
    amber: 'text-amber-700', zinc: 'text-zinc-900',
  };
  return (
    <div className="bg-white border border-zinc-200 p-4" data-testid={testId}>
      <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</span>
      <p className={`text-2xl font-bold mt-1 ${colorMap[color] || 'text-zinc-900'}`}>
        ₹{(value || 0).toLocaleString('en-IN')}
      </p>
    </div>
  );
};

const BreakdownCard = ({ label, budgeted, actual, testId }) => {
  const variance = actual !== null ? budgeted - actual : null;
  return (
    <div className="bg-white border border-zinc-200 p-4" data-testid={testId}>
      <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">{label}</h4>
      <div className="space-y-2">
        <div className="flex justify-between"><span className="text-sm text-zinc-600">Budgeted</span><span className="font-medium">₹{budgeted.toLocaleString('en-IN')}</span></div>
        {actual !== null && (
          <>
            <div className="flex justify-between"><span className="text-sm text-zinc-600">Actual</span><span className="font-medium">₹{actual.toLocaleString('en-IN')}</span></div>
            <div className="border-t border-zinc-200 pt-2 flex justify-between">
              <span className="text-sm text-zinc-600">Variance</span>
              <span className={`font-bold ${variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {variance < 0 ? '−' : '+'}₹{Math.abs(variance).toLocaleString('en-IN')}
              </span>
            </div>
          </>
        )}
        {actual === null && <p className="text-xs text-zinc-400">No actuals tracked yet</p>}
      </div>
    </div>
  );
};
